import { routes } from "@/global/routes";
import {
  faqs,
  howItWorks,
  SuitesContent,
  logeSuiteNames,
  TerracesContent,
  partySuiteNames,
  AnalyticsHeader,
  LogeBoxesContent,
  PartyTowersContent,
  inventoryCardSuiteModal,
  ColumbusBlueJacketsInventoryCard,
} from "./helpers";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";

const seatMap3dPreviewImage =
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/arena.jpg";
const bannerImage =
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/bluejackets_banner2.jpg";
const backgroundImage =
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/columbus-blue-jackets.webp";
const logoImage =
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/columbus-blue-jackets-logo.png";
const staticSeatMapImage =
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/seat-map.png";

import type { TeamConfig } from "../types";

const logeBoxesImages = [
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/Loge%20Boxes/LogeBox2.png",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/Loge%20Boxes/LogeBox4.png",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/Loge%20Boxes/LogeBox5.png",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/Loge%20Boxes/LogeBox6.png",
];

const suiteImages = [
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/Suites/Suites_DSC00199.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/Suites/Suites_DSC00216.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/Suites/Suites_DSC00231.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/Suites/Suites_DSC00235.jpg",
];

const terraceImages = [
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/Terrace%20Tables/TerraceTable2.png",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/Terrace%20Tables/TerraceTable3.png",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/Terrace%20Tables/TerraceTable4.png",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/Terrace%20Tables/TerraceTable1.png",
];

const partyTower24Images = [
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/24-Person%20Party%20Tower/24_party_tower_DSC09762%20%281%29.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/24-Person%20Party%20Tower/24_party_tower_DSC09835.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/24-Person%20Party%20Tower/24_party_tower_DSC09848%20%281%29.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/24-Person%20Party%20Tower/24_party_tower_DSC09882.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/24-Person%20Party%20Tower/24_party_tower_DSC09864.jpg",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/24-Person%20Party%20Tower/24_party_tower_DSC09803.jpg",
];

const clubAImages = [
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/club-a/ClubA-1.png",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/club-a/ClubA-2.png",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/club-a/ClubA-3.png",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/club-a/ClubA-4.png",
  "https://ss-leagues.s3.amazonaws.com/NHL/bluejackets/club-a/ClubA-5.png",
];

const config: TeamConfig = {
  teamSchema: "columbusbluejackets_suites",
  browserTitle: "Columbus Blue Jackets Suites",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Columbus Blue Jackets",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10212-bluejackets-sec",
    mapId: "blockmap2d",
    staticSeatMapImage: staticSeatMapImage,
    customSeatMapStaticImageStyle: {
      width: "80%",
      display: "block",
      marginX: "auto",
    },
    suiteImages: {
      LBA: logeBoxesImages,
      LBB: logeBoxesImages,
      LBC: logeBoxesImages,
      LBD: logeBoxesImages,
      LBE: logeBoxesImages,
      LBF: logeBoxesImages,
      LBG: logeBoxesImages,
      LBH: logeBoxesImages,
      LBI: logeBoxesImages,
      LBJ: logeBoxesImages,
      LBK: logeBoxesImages,
      LBL: logeBoxesImages,
      LBM: logeBoxesImages,
      LBN: logeBoxesImages,
      LBO: logeBoxesImages,
      LBP: logeBoxesImages,
      LBQ: logeBoxesImages,
      LBR: logeBoxesImages,
      LBS: logeBoxesImages,
      LBT: logeBoxesImages,
      LBU: logeBoxesImages,
      LBV: logeBoxesImages,
      LBW: logeBoxesImages,
      LBX: logeBoxesImages,
      LBY: logeBoxesImages,
      LBZ: logeBoxesImages,

      S1: suiteImages,
      S12: suiteImages,
      S13: suiteImages,
      S14: suiteImages,
      S15: suiteImages,
      S16: suiteImages,
      S17: suiteImages,
      S18: suiteImages,
      S19: suiteImages,
      S2: suiteImages,
      S20: suiteImages,
      S21: suiteImages,
      S22: suiteImages,
      S23: suiteImages,
      S24: suiteImages,
      S25: suiteImages,
      S26: suiteImages,
      S27: suiteImages,
      S28: suiteImages,
      S29: suiteImages,
      S3: suiteImages,
      S30: suiteImages,
      S31: suiteImages,
      S32: suiteImages,
      S33: suiteImages,
      S34: suiteImages,
      S35: suiteImages,
      S36: suiteImages,
      S37: suiteImages,
      S38: suiteImages,
      S39: suiteImages,
      S40: suiteImages,
      S41: suiteImages,
      S50: suiteImages,
      S51: suiteImages,
      S52: suiteImages,

      CT1: terraceImages,
      CT10: terraceImages,
      CT11: terraceImages,
      CT12: terraceImages,
      CT13: terraceImages,
      CT14: terraceImages,
      CT15: terraceImages,
      CT16: terraceImages,
      CT17: terraceImages,
      CT18: terraceImages,
      CT19: terraceImages,
      CT2: terraceImages,
      CT20: terraceImages,
      CT21: terraceImages,
      CT22: terraceImages,
      CT23: terraceImages,
      CT24: terraceImages,
      CT25: terraceImages,
      CT26: terraceImages,
      CT27: terraceImages,
      CT28: terraceImages,
      CT29: terraceImages,
      CT3: terraceImages,
      CT30: terraceImages,
      CT31: terraceImages,
      CT32: terraceImages,
      CT33: terraceImages,
      CT34: terraceImages,
      CT35: terraceImages,
      CT36: terraceImages,
      CT37: terraceImages,
      CT38: terraceImages,
      CT39: terraceImages,
      CT4: terraceImages,
      CT5: terraceImages,
      CT6: terraceImages,
      CT7: terraceImages,
      CT8: terraceImages,
      CT9: terraceImages,

      C1: partyTower24Images,
      "101": clubAImages,
    },
    hide3dViewFromSlider: ["C1", "101"],
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.suiteLevelV2,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,

    isCheckoutAdditionalDetailsEnabled: true,
    isPerksEnabled: true,
    invPreviewAccountGroup: ["0"],
    allowCheckoutFromPreview: true,

    checkout: {
      additionalDetails: {
        disclaimerFullNamePlaceholder: "Digital Signature",
      },
    },
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "G-XBHGVPC03F",
    attrkPixelId: "",
    beaconId: "",
    googleTagManagerId: "",
    trackGoogleAnalyticsOnCheckout: true,
    AnalyticsHeader,
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: false,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/bluejackets/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",
  shouldUseInternalLogin: false,

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: logoImage,
    giftCardStandalonePageImage: "",
    findGamesBannerImage: bannerImage,
    authBackgroundImage: backgroundImage,
    checkoutStatusPageImage: backgroundImage,
    seatMap3dPreviewImage: seatMap3dPreviewImage,
    logoImage: logoImage + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    hidePackageBalanceOnCheckout: true,
    hideServiceFee: true,

    inventoryCardSettings: {
      isPBLineItemsVisible: true,
      seatCountLabel: "Tickets Included",
      inventorySuitePackageInfo: [
        { suiteId: "TERRACE TABLE 30", FBPackageValue: "Included" },
        { suiteId: "TERRACE TABLE 31", FBPackageValue: "Included" },
        { suiteId: "TERRACE TABLE 32", FBPackageValue: "Included" },
        { suiteId: "TERRACE TABLE 33", FBPackageValue: "Included" },

        { suiteId: "SUITE  1", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  12", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  13", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  14", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  15", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  16", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  17", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  18", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  19", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  2", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  20", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  21", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  22", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  23", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  24", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  25", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  26", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  27", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  28", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  29", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  3", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  30", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  31", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  32", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  33", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  34", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  35", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  36", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  37", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  38", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  39", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  40", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  41", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  50", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  51", FBPackageValue: "Not Included" },
        { suiteId: "SUITE  52", FBPackageValue: "Not Included" },
        { suiteId: "LOGE A", FBPackageValue: "Not Included" },
        { suiteId: "LOGE C", FBPackageValue: "Not Included" },
        { suiteId: "LOGE B", FBPackageValue: "Not Included" },
        { suiteId: "LOGE D", FBPackageValue: "Not Included" },
        { suiteId: "LOGE E", FBPackageValue: "Not Included" },
        { suiteId: "LOGE F", FBPackageValue: "Not Included" },
        { suiteId: "LOGE G", FBPackageValue: "Not Included" },
        { suiteId: "LOGE H", FBPackageValue: "Not Included" },
        { suiteId: "LOGE I", FBPackageValue: "Not Included" },
        { suiteId: "LOGE K", FBPackageValue: "Not Included" },
        { suiteId: "LOGE L", FBPackageValue: "Not Included" },
        { suiteId: "LOGE M", FBPackageValue: "Not Included" },
        { suiteId: "LOGE N", FBPackageValue: "Not Included" },
        { suiteId: "LOGE O", FBPackageValue: "Not Included" },
        { suiteId: "LOGE Q", FBPackageValue: "Not Included" },
        { suiteId: "LOGE R", FBPackageValue: "Not Included" },
        { suiteId: "LOGE S", FBPackageValue: "Not Included" },
        { suiteId: "LOGE T", FBPackageValue: "Not Included" },
        { suiteId: "LOGE U", FBPackageValue: "Not Included" },
        { suiteId: "LOGE W", FBPackageValue: "Not Included" },
        { suiteId: "LOGE X", FBPackageValue: "Not Included" },
        { suiteId: "LOGE Y", FBPackageValue: "Not Included" },
        { suiteId: "LOGE Z", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 1", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 39", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 10", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 11", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 12", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 13", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 14", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 15", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 17", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 18", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 19", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 2", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 20", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 21", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 23", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 24", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 25", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 26", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 27", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 28", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 3", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 34", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 36", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 37", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 38", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 4", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 40", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 43", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 44", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 5", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 6", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 7", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 8", FBPackageValue: "Not Included" },
        { suiteId: "LOGE J", FBPackageValue: "Not Included" },
        { suiteId: "LOGE P", FBPackageValue: "Not Included" },
        { suiteId: "LOGE V", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 16", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 22", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 29", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 35", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 41", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 42", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 45", FBPackageValue: "Not Included" },
        { suiteId: "TERRACE TABLE 9", FBPackageValue: "Not Included" },
        { suiteId: "PARTY TOWER B", FBPackageValue: "Not Included" },
        { suiteId: "PARTY TOWER C", FBPackageValue: "Not Included" },
        { suiteId: "PARTY TOWER D", FBPackageValue: "Not Included" },
        { suiteId: "PARTY TOWER E", FBPackageValue: "Not Included" },
        { suiteId: "PARTY TOWER F", FBPackageValue: "Not Included" },
      ],
      pbLineItemsValue: 5,
      suiteLevelInventoryCard: {
        customButtons: [
          {
            id: 1,
            label: "More Info",
            onClick: () => {
              inventoryCardSuiteModal.getState().set({
                isOpen: true,
                title: "More Info",
                content: <LogeBoxesContent />,
              });
            },
            suiteNames: logeSuiteNames,
          },
          {
            id: 2,
            label: "More Info",
            onClick: () => {
              inventoryCardSuiteModal.getState().set({
                isOpen: true,
                title: "More Info",
                content: <SuitesContent />,
              });
            },
            suiteNames: Array.from({ length: 52 }, (_, i) => `SUITE  ${i + 1}`),
          },
          {
            id: 3,
            label: "More Info",
            onClick: () => {
              inventoryCardSuiteModal.getState().set({
                isOpen: true,
                title: "More Info",
                content: <TerracesContent />,
              });
            },
            suiteNames: Array.from(
              { length: 45 },
              (_, i) => `TERRACE TABLE ${i + 1}`
            ),
          },
          {
            id: 4,
            label: "More Info",
            onClick: () => {
              inventoryCardSuiteModal.getState().set({
                isOpen: true,
                title: "More Info",
                content: <PartyTowersContent />,
              });
            },
            suiteNames: partySuiteNames,
          },
        ],
        renderBody: [
          {
            id: 1,
            component: () => <ColumbusBlueJacketsInventoryCard />,
          },
        ],
      },
    },
    inventoryContainer: {
      getGridCellHeight: (arg) => (arg?.mdDown ? 260 : 180),
    },

    gameFiltersSettings: {
      hideSectionFilter: true,
      hideAccessibilityFilter: true,
    },

    checkoutPage: {
      checkoutForm: {
        optionalAdditionalDetailsCompanyName: true,
        optionalAdditionalDetailsCateringName: true,
        optionalAdditionalDetailsCateringEmail: true,
        optionalAdditionalDetailsCateringPhone: true,
        optionalAdditionalDetailsContractTitle: true,
        optionalAdditionalDetailsContractEmail: true,
        optionalAdditionalDetailsContractLastName: true,
        optionalAdditionalDetailsContractFirstName: true,
        optionalAdditionalDetailsDisclaimerFullName: true,
        optionalAdditionalDetailsContractPhoneNumber: true,

        sameAsContractSignerDefaultChecked: true,

        hideContractTitle: true,
        hideContractLastName: true,
        hideContractFirstName: true,
        hideCateringAndTicketContact: false,
      },
    },
  },

  colors: {
    primary: "#002654",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarText: "Select 1 game to continue!",
      progressBarSuccessText:
        "You're good to go! Add more events or select Continue to view seating options.",
      chooseSeatsTabTitle: "Choose Suite",
      progressBarAditionalText: " to continue!",
      suiteLanguageDisclaimer:
        "Please note that individual suites may vary from the photos displayed.",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",

    perksPage: {
      perkItemPriceLabel: "ticket",
    },

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
